import { CurrencyPipe } from '@angular/common';
import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoyaltyActionType, ScorecardV2 } from '@fgb/core';
import { FortressCurrencyPipe } from '@fgb/portal-component-library/src/lib/pipes';
import { Observable, map, merge, of } from 'rxjs';

/** Milestone action types (LoyaltyActionId) whose customer value is an amount spent, shown in the customer's currency */
export const SPEND_MILESTONE_ACTION_IDS: readonly number[] = [
  LoyaltyActionType.SKUPurchase,
  LoyaltyActionType.SKUBasketPurchase,
  LoyaltyActionType.PaymentGatewayTransaction,
];

/** True when the scorecard's milestone counts money spent rather than actions */
export function isScorecardV2SpendMilestone(scorecard: ScorecardV2 | null | undefined): boolean {
  const actionType = scorecard?.Milestone?.MilestoneActionType;
  return actionType != null && SPEND_MILESTONE_ACTION_IDS.includes(actionType);
}

/**
 * The milestone customer value as a progress label.
 * Spend milestones show the customer's currency and the spent text (e.g. "$15 spent"), all others show the plain value.
 *
 * @example
 * {{ scorecard | scorecardV2MilestoneValue | async }}
 */
@Pipe({ name: 'scorecardV2MilestoneValue' })
export class ScorecardV2MilestoneValuePipe implements PipeTransform {
  private translate = inject(TranslateService);
  /** Formats with the customer's currency, the same way as the rest of the portal */
  private currencyPipe = new FortressCurrencyPipe(inject(CurrencyPipe), this.translate);

  transform(scorecard: ScorecardV2 | null | undefined): Observable<string> {
    const value = scorecard?.Milestone?.CustomerValue;
    if (!isScorecardV2SpendMilestone(scorecard)) {
      return of(`${value ?? ''}`);
    }
    const amount = this.currencyPipe.transform(value ?? 0, undefined, undefined, '1.0-2');
    // fgbdefault keeps "spent" showing until the key is added in the Portal Admin
    const key = 'fgbcl.challenges.spent';
    const params = { fgbdefault: 'spent' };
    // Follows language changes and late loaded website content, like the translate pipe does
    return merge(
      this.translate.stream(key, params),
      this.translate.onTranslationChange.pipe(map(() => this.translate.instant(key, params)))
    ).pipe(map((spent: string) => `${amount} ${spent}`));
  }
}
